import { Button } from "@aurumiq/ui/components/button";
import { Input } from "@aurumiq/ui/components/input";
import { Switch } from "@aurumiq/ui/components/switch";
import { type ColumnDef, createColumnHelper, DataTable } from "@aurumiq/ui/composite/data-table";
import { ReasonDialog } from "@aurumiq/ui/composite/reason-dialog";
import { StatusTag } from "@aurumiq/ui/composite/status-tag";
import { relativeTime } from "@aurumiq/ui/lib/format";
import { KeyRound, UserPlus } from "lucide-react";
import { useMemo, useState } from "react";
import { PageHeader } from "@/app/page-header";
import { usePermission } from "@/auth/use-permission";
import { NativeSelect } from "@/components/field";
import { STAFF_ROLES, usePatchStaff, useStaff } from "@/features/staff/queries";
import { AddStaffDialog, SetPasswordDialog } from "@/features/staff/staff-dialogs";
import { errorMessage } from "@/lib/api";
import type { Staff } from "@/lib/types";

const col = createColumnHelper<Staff>();

function YesNo({ value }: { value: boolean }) {
  return value ? <StatusTag variant="ok">YES</StatusTag> : <StatusTag>NO</StatusTag>;
}

export function StaffPage() {
  const canWrite = usePermission("staff:write");
  const [q, setQ] = useState("");
  const staff = useStaff(q);
  const patch = usePatchStaff();
  const [addOpen, setAddOpen] = useState(false);
  const [pwFor, setPwFor] = useState<Staff | null>(null);
  const [toggling, setToggling] = useState<Staff | null>(null);

  const columns = useMemo(
    () => [
      col.accessor("email", {
        header: "Email",
        cell: (c) => (
          <div>
            <div className="font-semibold">{c.getValue()}</div>
            <div className="text-muted-foreground text-xs">{c.row.original.display_name ?? ""}</div>
          </div>
        ),
      }),
      col.accessor("role", {
        header: "Role",
        cell: (c) =>
          canWrite ? (
            <NativeSelect
              aria-label={`Role for ${c.row.original.email}`}
              className="h-7 w-36 text-xs"
              value={c.getValue()}
              onChange={(e) =>
                patch.mutate({ id: c.row.original.id, role: e.target.value as Staff["role"] })
              }
            >
              {STAFF_ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </NativeSelect>
          ) : (
            c.getValue()
          ),
      }),
      col.accessor("is_active", {
        header: "Active",
        cell: (c) =>
          canWrite ? (
            <Switch
              aria-label={`${c.getValue() ? "Deactivate" : "Activate"} ${c.row.original.email}`}
              checked={c.getValue()}
              onCheckedChange={() => setToggling(c.row.original)}
            />
          ) : (
            <YesNo value={c.getValue()} />
          ),
      }),
      col.accessor("has_password", {
        header: "Local password",
        cell: (c) => <YesNo value={c.getValue()} />,
      }),
      col.accessor("entra_oid", {
        header: "Entra linked",
        cell: (c) => <YesNo value={!!c.getValue()} />,
      }),
      col.accessor("last_login_at", {
        header: "Last login",
        cell: (c) => <span title={c.getValue() ?? undefined}>{relativeTime(c.getValue())}</span>,
      }),
      col.display({
        id: "actions",
        header: "",
        meta: { numeric: true },
        cell: (c) =>
          canWrite ? (
            <Button variant="ghost" size="sm" onClick={() => setPwFor(c.row.original)}>
              <KeyRound /> Set password
            </Button>
          ) : null,
      }),
    ],
    [canWrite, patch],
  );

  return (
    <>
      <PageHeader
        title="Staff"
        description="Admin panel users, their role and sign-in methods."
        actions={
          canWrite ? (
            <Button variant="gold" onClick={() => setAddOpen(true)}>
              <UserPlus /> Add staff
            </Button>
          ) : null
        }
      />
      <DataTable
        columns={columns as ColumnDef<Staff, unknown>[]}
        data={staff.data?.items ?? []}
        isLoading={staff.isPending}
        getRowId={(s) => s.id}
        emptyMessage={
          staff.isError ? `Could not load staff: ${errorMessage(staff.error)}` : "No staff."
        }
        toolbar={
          <Input
            placeholder="Search email or name…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="max-w-xs"
          />
        }
      />
      <AddStaffDialog open={addOpen} onOpenChange={setAddOpen} />
      <SetPasswordDialog staff={pwFor} onOpenChange={(o) => !o && setPwFor(null)} />
      <ReasonDialog
        open={toggling !== null}
        onOpenChange={(o) => !o && setToggling(null)}
        title={`${toggling?.is_active ? "Deactivate" : "Activate"} ${toggling?.email}?`}
        description={
          toggling?.is_active
            ? "They are signed out everywhere and cannot sign in until reactivated."
            : "Restores sign-in with their current role."
        }
        confirmLabel={toggling?.is_active ? "Deactivate" : "Activate"}
        confirmVariant={toggling?.is_active ? "destructive" : "default"}
        onConfirm={async (reason) => {
          if (toggling)
            await patch.mutateAsync({ id: toggling.id, is_active: !toggling.is_active, reason });
        }}
      />
    </>
  );
}
